import React, { useState, useEffect, useCallback, useContext } from 'react';
import '../dashboard/Dashboard.css';
import styled from 'styled-components';
import { 
  getFlowRuntimes, 
  getFlowStats, 
  stopFlowRuntime, 
  forceDeleteFlowRuntime, 
  bulkStopFlows,
  getFlowRuntimeLogs,
  clearFlowRuntimeLogs,
  cleanupOldLogs
} from '../api/flowRuntimeAPI';
import { FaPlay, FaStop, FaTrash, FaEye, FaCogs, FaChartLine, FaSync, FaCheck, FaExclamationTriangle } from 'react-icons/fa';
import { AuthContext } from '../App.js';

// New function to parse JSON logs
const parseJsonLog = (log) => {
  // Try to parse the message field as JSON
  try {
    const parsed = typeof log.message === 'string' ? JSON.parse(log.message) : log.message;
    return parsed;
  } catch {
    // If not JSON, fallback to old parsing for legacy logs
    return parseLogMessage(log.message);
        }
      };

// Legacy function for old string-based logs
const parseLogMessage = (message) => {
      return {
    type: 'legacy',
    displayMessage: message,
    showMetadata: true,
    importance: 'low'
  };
};

// Helper function to group logs by run_id and create node execution groups
const groupLogsByNodeExecution = (logs) => {
  const nodeExecutions = {};
  const systemLogs = [];
  
  logs.forEach((log) => {
    try {
      const parsed = typeof log.message === 'string' ? JSON.parse(log.message) : log.message;
      
      if (parsed.run_id) {
        if (!nodeExecutions[parsed.run_id]) {
          nodeExecutions[parsed.run_id] = {
            node_id: parsed.node_id,
            node_name: parsed.node_name || 'Unknown Node',
            node_type: parsed.node_type || 'unknown',
            run_id: parsed.run_id,
            logs: [],
            start_time: null,
            end_time: null,
            processing_time: null,
            status: 'running',
            params: {},
            result: {}
      };
    }
        
        const execution = nodeExecutions[parsed.run_id];
        execution.logs.push({ ...log, parsed });
        
        // Update execution metadata based on log type
        if (parsed.type === 'node_start') {
          execution.start_time = parsed.start_time;
          execution.params = parsed.params || {};
          execution.node_type = parsed.node_type || execution.node_type;
        } else if (parsed.type === 'node_complete') {
          execution.end_time = parsed.end_time;
          execution.processing_time = parsed.processing_time;
          execution.status = getNodeStatus(parsed);
          execution.result = extractNodeResult(parsed);
      }
      } else {
        // System logs without run_id
        systemLogs.push({ ...log, parsed: { type: 'system', message: log.message } });
      }
    } catch {
      // Legacy logs
      systemLogs.push({ ...log, parsed: { type: 'legacy', message: log.message } });
    }
  });
  
  return { nodeExecutions: Object.values(nodeExecutions), systemLogs };
};

// Helper to determine node status from completion log
const getNodeStatus = (completionLog) => {
  if (completionLog.error || 
      completionLog.execution_success === false ||
      completionLog.operation_success === false ||
      completionLog.email_success === false ||
      completionLog.search_success === false ||
      completionLog.download_success === false ||
      completionLog.add_success === false) {
    return 'failed';
  }
  return 'completed';
};

// Helper to extract key results from node completion
const extractNodeResult = (completionLog) => {
  const result = {};
  
  // Extract key fields based on node type
  if (completionLog.variable_set) result.variable_set = completionLog.variable_set;
  if (completionLog.value_assigned) result.value_assigned = completionLog.value_assigned;
  if (completionLog.recognition_result !== undefined) result.recognition_result = completionLog.recognition_result;
  if (completionLog.selected_option) result.selected_option = completionLog.selected_option;
  if (completionLog.number_response !== undefined) result.number = completionLog.number_response;
  if (completionLog.response_length) result.response_length = completionLog.response_length;
  if (completionLog.generated_content) result.generated_content = completionLog.generated_content;
  if (completionLog.evaluation_result !== undefined) result.evaluation_result = completionLog.evaluation_result;
  if (completionLog.output_path) result.output_path = completionLog.output_path;
  
  return result;
};



// Get icon class for node type (matching FlowBuilder.html exactly)
const getNodeIconClass = (nodeType) => {
  const iconMap = {
    'start': 'fas fa-play',
    'recognize': 'fas fa-eye',
    'list_match': 'fas fa-circle-dot',
    'respond': 'fas fa-comment-dots',
    'generate': 'fas fa-pencil-alt',
    'capture': 'fas fa-search',
    'email': 'fas fa-envelope',
    'act': 'fas fa-code',
    'parallel': 'fas fa-project-diagram',
    'if_then': 'fas fa-code-branch',
    'zapier': 'fas fa-link',
    'website': 'fas fa-cloud-download-alt',
    'google_search': 'fas fa-globe',
    'run_flow': 'fas fa-sync-alt',
    'wait': 'fas fa-hourglass-half',
    'storage': 'fas fa-folder-open',
    'add_vault': 'fas fa-vault',
    'number': 'fas fa-hashtag',
    'variable': 'fas fa-equals',
    'noresponse': 'fas fa-ban'
      };
  return iconMap[nodeType] || 'fas fa-cog';
};

// Styled Components following the same pattern as other pages
const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
  table-layout: fixed;

  th, td {
    padding: 10px;
    font-size: 1.1rem;
    border: 1px solid ${props => props.isDarkMode ? '#1a1a1a' : '#e5e7eb'};
    text-align: center;
    vertical-align: middle;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: normal;
    color: ${props => props.isDarkMode ? '#e0e0e0' : '#374151'};
  }

  tr:nth-child(even) {
    background-color: ${props => props.isDarkMode ? '#0a0a0a' : '#ffffff'};
  }

  tr:hover {
    background-color: ${props => props.isDarkMode ? 'rgba(0,214,122,0.05)' : 'rgba(0,214,122,0.05)'};
  }

  th:nth-child(1), td:nth-child(1) { width: 8%; }
  th:nth-child(2), td:nth-child(2) { width: 20%; }
  th:nth-child(3), td:nth-child(3) { width: 12%; }
  th:nth-child(4), td:nth-child(4) { width: 20%; }
  th:nth-child(5), td:nth-child(5) { width: 15%; }
  th:nth-child(6), td:nth-child(6) { width: 12%; }
  th:nth-child(7), td:nth-child(7) { width: 13%; }

  @media (max-width: 768px) {
    th, td {
      padding: 5px;
      font-size: 0.9rem;
    }
  }
`;

const StyledTableContainer = styled.div`
  padding: 20px;
  overflow-x: auto;

  @media (max-width: 768px) {
    padding: 0px;
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const StatCard = styled.div`
  background-color: ${props => props.isDarkMode ? '#0a0a0a' : '#ffffff'};
  border: 1px solid ${props => props.isDarkMode ? '#1a1a1a' : '#e5e7eb'};
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  color: ${props => props.isDarkMode ? '#e0e0e0' : '#374151'};

  h3 {
    margin: 10px 0 5px 0;
    color: ${props => props.isDarkMode ? '#e0e0e0' : '#374151'};
  }

  .stat-value {
    font-size: 2rem;
    font-weight: bold;
    color: #00d67a;
  }

  .stat-subtitle {
    color: ${props => props.isDarkMode ? '#999999' : '#6b7280'};
    font-size: 0.9rem;
  }
`;

const BulkActionsContainer = styled.div`
  background-color: ${props => props.isDarkMode ? '#0a0a0a' : '#ffffff'};
  border: 1px solid ${props => props.isDarkMode ? '#1a1a1a' : '#e5e7eb'};
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TabContainer = styled.div`
  display: flex;
  margin-bottom: 20px;
  border-bottom: 1px solid ${props => props.isDarkMode ? '#1a1a1a' : '#e5e7eb'};
`;

const Tab = styled.button`
  background: none;
  border: none;
  padding: 15px 30px;
  font-size: 1.1rem;
  cursor: pointer;
  color: ${props => props.isDarkMode ? '#e0e0e0' : '#374151'};
  border-bottom: 3px solid transparent;
  transition: all 0.3s ease;

  &.active {
    border-bottom-color: #00d67a;
    color: #00d67a;
    font-weight: bold;
  }

  &:hover {
    background-color: ${props => props.isDarkMode ? '#0a0a0a' : '#ffffff'};
  }
`;

const FlowRuntimeDashboard = ({ isDarkMode, styles, isMobile, wait }) => {
  const { vectorVault } = useContext(AuthContext);
  const [allFlowRuntimes, setAllFlowRuntimes] = useState([]);
  const [flowStats, setFlowStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFlows, setSelectedFlows] = useState([]);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('active'); // 'active' or 'past'
  const [stoppingFlows, setStoppingFlows] = useState(new Set()); // Track which flows are being stopped
  const [expandedRowId, setExpandedRowId] = useState(null); // Track which row is expanded
  const [rowLogs, setRowLogs] = useState({}); // Store logs for each row
  const [expandedNodes, setExpandedNodes] = useState(new Set()); // Track expanded nodes in new JSON view

  // Filter flows based on active tab
  const getFilteredFlows = () => {
    if (activeTab === 'active') {
      return allFlowRuntimes.filter(flow => flow.status === 'active');
    } else {
      return allFlowRuntimes.filter(flow => flow.status === 'completed' || flow.status === 'stopped');
    }
  };

  const flowRuntimes = getFilteredFlows();

  // Fetch flow runtimes data
  const fetchFlowRuntimes = useCallback(async () => {
    if (!vectorVault || wait) {
      if (wait) {
        setError('Initializing authentication...');
      } else {
        setError('VectorVault instance not available');
      }
      setLoading(false);
      return;
    }

    try {
      const [runtimesResponse, statsResponse] = await Promise.all([
        getFlowRuntimes(vectorVault),
        getFlowStats(vectorVault)
      ]);
      
      setAllFlowRuntimes(runtimesResponse.flow_runtimes || []);
      setFlowStats(statsResponse.stats || {});
      setLastRefresh(new Date());
      setError(null);
    } catch (err) {
      setError('Failed to fetch agent runtimes: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [vectorVault, wait]);

  // Auto-refresh effect (only for active tab)
  useEffect(() => {
    if (!wait) {
      fetchFlowRuntimes();
    }
    
    let interval;
    if (autoRefresh && activeTab === 'active' && !wait) {
      interval = setInterval(fetchFlowRuntimes, 5000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, activeTab, fetchFlowRuntimes, wait]);

  // Handle flow selection for bulk operations
  const handleFlowSelection = (runtimeId, isSelected) => {
    if (isSelected) {
      setSelectedFlows([...selectedFlows, runtimeId]);
    } else {
      setSelectedFlows(selectedFlows.filter(id => id !== runtimeId));
    }
  };

  // Handle select all flows
  const handleSelectAll = (selectAll) => {
    if (selectAll) {
      setSelectedFlows(flowRuntimes.map(flow => flow.runtime_id));
    } else {
      setSelectedFlows([]);
    }
  };

  // Handle stopping a single flow
  const handleStopFlow = async (runtimeId) => {
    if (!vectorVault || wait) {
      setError(wait ? 'Authentication not ready' : 'VectorVault instance not available');
      return;
    }

    // Add to stopping flows set
    setStoppingFlows(prev => new Set([...prev, runtimeId]));

    try {
      await stopFlowRuntime(runtimeId, vectorVault);
      fetchFlowRuntimes();
    } catch (err) {
      setError('Failed to stop agent: ' + err.message);
    } finally {
      // Remove from stopping flows set
      setStoppingFlows(prev => {
        const newSet = new Set(prev);
        newSet.delete(runtimeId);
        return newSet;
      });
    }
  };

  // Handle force deleting a flow
  const handleForceDelete = async (runtimeId) => {
    if (!vectorVault || wait) {
      setError(wait ? 'Authentication not ready' : 'VectorVault instance not available');
      return;
    }

    if (window.confirm('Are you sure you want to force delete this agent? This action cannot be undone.')) {
      try {
        await forceDeleteFlowRuntime(runtimeId, vectorVault);
        fetchFlowRuntimes();
      } catch (err) {
        setError('Failed to force delete agent: ' + err.message);
      }
    }
  };

  // Handle bulk stop flows
  const handleBulkStop = async () => {
    if (selectedFlows.length === 0) return;
    if (!vectorVault || wait) {
      setError(wait ? 'Authentication not ready' : 'VectorVault instance not available');
      return;
    }
    
    setBulkActionLoading(true);
    try {
      await bulkStopFlows(selectedFlows, vectorVault);
      setSelectedFlows([]);
      fetchFlowRuntimes();
    } catch (err) {
      setError('Failed to bulk stop agents: ' + err.message);
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Handle bulk delete flows
  const handleBulkDelete = async () => {
    if (selectedFlows.length === 0) return;
    if (!vectorVault || wait) {
      setError(wait ? 'Authentication not ready' : 'VectorVault instance not available');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to force delete ${selectedFlows.length} agent(s)? This action cannot be undone.`)) {
      return;
    }
    
    setBulkActionLoading(true);
    try {
      // Delete flows one by one since there's no bulk delete endpoint
      const deletePromises = selectedFlows.map(runtimeId => 
        forceDeleteFlowRuntime(runtimeId, vectorVault)
      );
      await Promise.all(deletePromises);
      setSelectedFlows([]);
      fetchFlowRuntimes();
    } catch (err) {
      setError('Failed to bulk delete agents: ' + err.message);
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Handle viewing flow details (inline expansion)
  const handleViewDetails = async (flow) => {
    if (!vectorVault || wait) {
      setError(wait ? 'Authentication not ready' : 'VectorVault instance not available');
      return;
    }

    // If clicking on already expanded row, collapse it
    if (expandedRowId === flow.runtime_id) {
      setExpandedRowId(null);
      return;
    }

    setExpandedRowId(flow.runtime_id);
    
    // If we already have logs for this row, don't fetch again
    if (rowLogs[flow.runtime_id]) {
      return;
    }
    
    try {
      const logsResponse = await getFlowRuntimeLogs(flow.runtime_id, vectorVault);
      setRowLogs(prev => ({
        ...prev,
        [flow.runtime_id]: logsResponse.logs || []
      }));
    } catch (err) {
      console.error('Failed to fetch logs:', err);
      setRowLogs(prev => ({
        ...prev,
        [flow.runtime_id]: []
      }));
    }
  };

  // Handle clearing logs for a specific runtime
  const handleClearLogs = async (runtimeId) => {
    if (!vectorVault || wait) return;
    
    try {
      await clearFlowRuntimeLogs(runtimeId, vectorVault);
      setRowLogs(prev => ({
        ...prev,
        [runtimeId]: []
      }));
    } catch (err) {
      setError('Failed to clear logs: ' + err.message);
    }
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedFlows([]); // Clear selection when switching tabs
    setExpandedRowId(null); // Close any expanded logs when switching tabs
  };

  // Status badge component
  const StatusBadge = ({ status }) => {
    let badgeStyle = {
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '0.8rem',
      fontWeight: 'bold',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px'
    };

    let icon;
    switch (status) {
      case 'active':
        badgeStyle.backgroundColor = '#28a745';
        badgeStyle.color = 'white';
        icon = <FaPlay />;
        break;
      case 'completed':
        badgeStyle.backgroundColor = '#00d67a';
        badgeStyle.color = 'white';
        icon = <FaCheck />;
        break;
      case 'stopped':
        badgeStyle.backgroundColor = '#ffc107';
        badgeStyle.color = 'black';
        icon = <FaExclamationTriangle />;
        break;
      default:
        badgeStyle.backgroundColor = '#6c757d';
        badgeStyle.color = 'white';
        icon = <FaStop />;
    }
    
    return (
      <span style={badgeStyle}>
        {icon} {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Format duration to show live updates for active flows or final duration for past flows
  const formatDuration = (flow) => {
    if (flow.status === 'active') {
      const now = Date.now() / 1000;
      const totalSeconds = Math.floor(now - flow.started_at);
      const nodeSeconds = Math.floor(now - (flow.current_node_started_at || flow.started_at));
      
      const formatTime = (seconds) => {
        if (seconds < 60) return `${seconds}s`;
        if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        return `${hours}h ${minutes}m`;
      };
      
      return {
        total: formatTime(totalSeconds),
        node: formatTime(nodeSeconds)
      };
    } else {
      // For completed/stopped flows, use the final duration
      return {
        total: flow.total_duration_formatted || '0s',
        node: 'Final'
      };
    }
  };

  // Format end date and time for past flows
  const formatEndDateTime = (flow) => {
    if (flow.status === 'active') {
      return null; // Not applicable for active flows
    }
    
    // Calculate end time: start_time + duration_in_seconds
    const endTimestamp = flow.started_at + (flow.total_duration_seconds || 0);
    const endDate = new Date(endTimestamp * 1000);
    
    // Format as readable date and time
    const timeOptions = { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    };
    const dateOptions = { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    };
    
    return {
      time: endDate.toLocaleTimeString([], timeOptions),
      date: endDate.toLocaleDateString([], dateOptions)
    };
  };

  // Get stats for current tab
  const getTabStats = () => {
    const activeFlows = allFlowRuntimes.filter(flow => flow.status === 'active');
    const completedFlows = allFlowRuntimes.filter(flow => flow.status === 'completed');
    const stoppedFlows = allFlowRuntimes.filter(flow => flow.status === 'stopped');
    
    if (activeTab === 'active') {
      return {
        total_active: activeFlows.length,
        total_stopping: activeFlows.filter(flow => flow.should_stop).length,
        total_parent_flows: activeFlows.filter(flow => flow.is_parent_flow).length,
        total_child_flows: activeFlows.filter(flow => !flow.is_parent_flow).length,
        ...flowStats
      };
    } else {
      return {
        total_completed: completedFlows.length,
        total_stopped: stoppedFlows.length,
        total_parent_flows: [...completedFlows, ...stoppedFlows].filter(flow => flow.is_parent_flow).length,
        total_child_flows: [...completedFlows, ...stoppedFlows].filter(flow => !flow.is_parent_flow).length,
        completion_rate: completedFlows.length + stoppedFlows.length > 0 
          ? Math.round((completedFlows.length / (completedFlows.length + stoppedFlows.length)) * 100) 
          : 0
      };
    }
  };

  const tabStats = getTabStats();

  if (loading) {
    return (
      <div style={styles.wrapper}>
        <div className="App" style={{...styles.body, paddingTop: "50px"}}>
          <div className="loading-spinner" style={{ display: 'block' }}>
            <svg viewBox="0 0 50 50">
              <circle cx="25" cy="25" r="20" stroke="#00d67a" strokeWidth="5" fill="none" />
            </svg>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.wrapper}>
      <div className="App" style={{...styles.body, paddingTop: "50px"}}>
        <h1 style={{ marginBottom: '10px' }}>Agent Runtime Dashboard</h1>
        <p style={{ marginBottom: '30px' }}>
          Monitor and manage your agent lifecycle. <strong>Active</strong> shows currently running agents with real-time monitoring. 
          <strong>Past</strong> shows completed and stopped agents with preserved metadata and logs for analysis.
        </p>

        {/* Tabs */}
        <TabContainer isDarkMode={isDarkMode}>
          <Tab 
            isDarkMode={isDarkMode}
            className={activeTab === 'active' ? 'active' : ''}
            onClick={() => handleTabChange('active')}
          >
            <FaPlay style={{ marginRight: '8px' }} />
            Active Agents ({allFlowRuntimes.filter(flow => flow.status === 'active').length})
          </Tab>
          <Tab 
            isDarkMode={isDarkMode}
            className={activeTab === 'past' ? 'active' : ''}
            onClick={() => handleTabChange('past')}
          >
            <FaChartLine style={{ marginRight: '8px' }} />
            Past Agents ({allFlowRuntimes.filter(flow => flow.status === 'completed' || flow.status === 'stopped').length})
          </Tab>
        </TabContainer>

        {/* Header Controls */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <span style={{ marginRight: '10px' }}>
              Last updated: {lastRefresh.toLocaleTimeString()}
            </span>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            {activeTab === 'active' && (
              <button
                className={autoRefresh ? 'btn-light' : 'btn-dark'}
                onClick={() => setAutoRefresh(!autoRefresh)}
                style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
              >
                <FaSync />
                {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
              </button>
            )}
            <button 
              className="button" 
              onClick={fetchFlowRuntimes}
              style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
            >
              <FaSync /> Refresh
            </button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div style={{ 
            backgroundColor: '#f8d7da', 
            color: '#721c24', 
            padding: '10px', 
            borderRadius: '4px', 
            marginBottom: '20px',
            border: '1px solid #f5c6cb'
          }}>
            {error}
            <button 
              style={{ float: 'right', background: 'none', border: 'none', fontSize: '16px', cursor: 'pointer' }}
              onClick={() => setError(null)}
            >
              ×
            </button>
          </div>
        )}

        {/* Stats Overview */}
        <StatsContainer>
          {activeTab === 'active' ? (
            <>
              <StatCard isDarkMode={isDarkMode}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <FaPlay style={{ color: '#28a745' }} />
                  <h3>Active Agents</h3>
                </div>
                <div className="stat-value">{tabStats.total_active || 0}</div>
                <div className="stat-subtitle">{tabStats.total_stopping || 0} stopping</div>
              </StatCard>

              <StatCard isDarkMode={isDarkMode}>
                <h3>Parent Agents</h3>
                <div className="stat-value">{tabStats.total_parent_flows || 0}</div>
                <div className="stat-subtitle">{tabStats.total_child_flows || 0} child agents</div>
              </StatCard>

              <StatCard isDarkMode={isDarkMode}>
                <h3>Average Runtime</h3>
                <div className="stat-value">{flowStats.average_runtime_formatted || '0m'}</div>
              </StatCard>

              <StatCard isDarkMode={isDarkMode}>
                <h3>Longest Running</h3>
                <div className="stat-value">{flowStats.longest_running_duration_formatted || '0m'}</div>
                {flowStats.longest_running_flow && (
                  <div className="stat-subtitle">{flowStats.longest_running_flow.flow_name}</div>
                )}
              </StatCard>
            </>
          ) : (
            <>
              <StatCard isDarkMode={isDarkMode}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <FaCheck style={{ color: '#00d67a' }} />
                  <h3>Completed</h3>
                </div>
                <div className="stat-value">{tabStats.total_completed || 0}</div>
                <div className="stat-subtitle">Successfully finished</div>
              </StatCard>

              <StatCard isDarkMode={isDarkMode}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <FaExclamationTriangle style={{ color: '#ffc107' }} />
                  <h3>Stopped</h3>
                </div>
                <div className="stat-value">{tabStats.total_stopped || 0}</div>
                <div className="stat-subtitle">Manually stopped or error</div>
              </StatCard>

              <StatCard isDarkMode={isDarkMode}>
                <h3>Success Rate</h3>
                <div className="stat-value">{tabStats.completion_rate}%</div>
                <div className="stat-subtitle">Completion vs stopped</div>
              </StatCard>

              <StatCard isDarkMode={isDarkMode}>
                <h3>Parent Agents</h3>
                <div className="stat-value">{tabStats.total_parent_flows || 0}</div>
                <div className="stat-subtitle">{tabStats.total_child_flows || 0} child agents</div>
              </StatCard>
            </>
          )}
        </StatsContainer>

        {/* Bulk Actions - only show for active flows that can be stopped */}
        {selectedFlows.length > 0 && activeTab === 'active' && (
          <BulkActionsContainer isDarkMode={isDarkMode}>
            <span>{selectedFlows.length} agent(s) selected</span>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                className="btn-dark"
                onClick={handleBulkStop}
                disabled={bulkActionLoading}
                style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
              >
                {bulkActionLoading ? (
                  <div className="loading-spinner" style={{ display: 'inline-block', width: '16px', height: '16px' }}>
                    <svg viewBox="0 0 50 50" style={{ width: '16px', height: '16px' }}>
                      <circle cx="25" cy="25" r="20" stroke="#00d67a" strokeWidth="5" fill="none" />
                    </svg>
                  </div>
                ) : (
                  <FaStop />
                )}
                Stop Selected
              </button>
              <button
                className="btn-dark"
                onClick={handleBulkDelete}
                disabled={bulkActionLoading}
                style={{ display: 'flex', alignItems: 'center', gap: '5px', backgroundColor: '#dc3545' }}
              >
                {bulkActionLoading ? (
                  <div className="loading-spinner" style={{ display: 'inline-block', width: '16px', height: '16px' }}>
                    <svg viewBox="0 0 50 50" style={{ width: '16px', height: '16px' }}>
                      <circle cx="25" cy="25" r="20" stroke="#00d67a" strokeWidth="5" fill="none" />
                    </svg>
                  </div>
                ) : (
                  <FaTrash />
                )}
                Delete Selected
              </button>
              <button
                className="btn-light"
                onClick={() => setSelectedFlows([])}
              >
                Clear Selection
              </button>
            </div>
          </BulkActionsContainer>
        )}

        {/* Main Table */}
        <StyledTableContainer>
          <StyledTable isDarkMode={isDarkMode}>
            <thead>
              <tr>
                {activeTab === 'active' && (
                  <th>
                    <input
                      type="checkbox"
                      className={isDarkMode ? 'sleek-checkbox-dark' : 'sleek-checkbox-light'}
                      checked={selectedFlows.length === flowRuntimes.length && flowRuntimes.length > 0}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  </th>
                )}
                <th>Agent Name</th>
                <th>Status</th>
                <th>{activeTab === 'active' ? 'Current Node' : 'Ended'}</th>
                <th>Duration</th>
                <th>Invoke Method</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {flowRuntimes.length === 0 ? (
                <tr>
                  <td colSpan={activeTab === 'active' ? "7" : "6"} style={{ padding: '40px', color: '#999999' }}>
                    No {activeTab} agent runtimes found
                  </td>
                </tr>
              ) : (
                flowRuntimes.map((flow) => {
                  const duration = formatDuration(flow);
                  const isExpanded = expandedRowId === flow.runtime_id;
                  const logs = rowLogs[flow.runtime_id] || [];
                  
                  return (
                    <React.Fragment key={flow.runtime_id}>
                      <tr>
                        {activeTab === 'active' && (
                          <td>
                            <input
                              type="checkbox"
                              className={isDarkMode ? 'sleek-checkbox-dark' : 'sleek-checkbox-light'}
                              checked={selectedFlows.includes(flow.runtime_id)}
                              onChange={(e) => handleFlowSelection(flow.runtime_id, e.target.checked)}
                            />
                          </td>
                        )}
                        <td>
                          <strong>{flow.flow_name}</strong>
                          {flow.is_parent_flow && (
                            <div style={{ 
                              backgroundColor: '#17a2b8', 
                              color: 'white', 
                              padding: '2px 6px', 
                              borderRadius: '4px', 
                              fontSize: '0.7rem', 
                              marginTop: '4px',
                              display: 'inline-block'
                            }}>
                              Parent
                            </div>
                          )}
                        </td>
                        <td>
                          <StatusBadge status={flow.status} />
                        </td>
                        <td>
                          {activeTab === 'active' ? (
                            <div>
                              <strong>{flow.current_node_name || 'Completed'}</strong>
                              {flow.current_node_id && (
                                <>
                                  <br />
                                  <small style={{ color: '#999999' }}>{flow.current_node_id}</small>
                                </>
                              )}
                            </div>
                          ) : (
                            <div>
                              <strong>{formatEndDateTime(flow)?.time}</strong>
                              <br />
                              <small style={{ color: '#999999' }}>{formatEndDateTime(flow)?.date}</small>
                            </div>
                          )}
                        </td>
                        <td>
                          <div>
                            <strong>Total: {duration.total}</strong>
                            {activeTab === 'active' && (
                              <>
                                <br />
                                <small style={{ color: '#999999' }}>Node: {duration.node}</small>
                              </>
                            )}
                          </div>
                        </td>
                        <td>
                          <div style={{ 
                            backgroundColor: '#6c757d', 
                            color: 'white', 
                            padding: '4px 8px', 
                            borderRadius: '4px', 
                            fontSize: '0.8rem' 
                          }}>
                            {flow.invoke_method}
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                            <button
                              className={isExpanded ? "btn-dark" : "btn-light"}
                              onClick={() => handleViewDetails(flow)}
                              style={{ padding: '5px 8px' }}
                            >
                              <FaEye />
                            </button>
                            {activeTab === 'active' && flow.status === 'active' && (
                              <>
                                <button
                                  className="btn-dark"
                                  onClick={() => handleStopFlow(flow.runtime_id)}
                                  disabled={flow.should_stop || stoppingFlows.has(flow.runtime_id)}
                                  style={{ padding: '5px 8px' }}
                                >
                                  {stoppingFlows.has(flow.runtime_id) ? (
                                    <div className="loading-spinner" style={{ display: 'inline-block', width: '14px', height: '14px' }}>
                                      <svg viewBox="0 0 50 50" style={{ width: '14px', height: '14px' }}>
                                        <circle cx="25" cy="25" r="20" stroke="#00d67a" strokeWidth="5" fill="none" />
                                      </svg>
                                    </div>
                                  ) : (
                                    <FaStop />
                                  )}
                                </button>
                                <button
                                  className="btn-dark"
                                  onClick={() => handleForceDelete(flow.runtime_id)}
                                  style={{ padding: '5px 8px', backgroundColor: '#dc3545' }}
                                >
                                  <FaTrash />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                      
                      {/* Expanded logs row */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={activeTab === 'active' ? "7" : "6"} style={{ 
                            padding: '0', 
                            backgroundColor: isDarkMode ? '#0d0d0d' : '#ffffff',
                            border: 'none'
                          }}>
                            <div style={{ 
                              padding: '20px',
                              borderTop: `2px solid ${isDarkMode ? '#1a1a1a' : '#e5e7eb'}`
                            }}>
                              {/* Agent Details */}
                              <div style={{ marginBottom: '20px' }}>
                                <h4 style={{ margin: '0 0 15px 0', color: isDarkMode ? '#e0e0e0' : '#374151' }}>
                                  Agent Runtime Details - {flow.flow_name}
                                </h4>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '15px' }}>
                                  <div>
                                    <strong>Runtime ID:</strong> 
                                    <div style={{ color: isDarkMode ? '#999999' : '#6b7280', fontSize: '0.9rem', fontFamily: 'monospace' }}>
                                      {flow.runtime_id}
                                    </div>
                                  </div>
                                  <div>
                                    <strong>Status:</strong> 
                                    <div style={{ marginTop: '4px' }}>
                                      <StatusBadge status={flow.status} />
                                    </div>
                                  </div>
                                  <div>
                                    <strong>Invoke Method:</strong>
                                    <div style={{ marginTop: '4px' }}>
                                      <span style={{ 
                                        backgroundColor: '#6c757d', 
                                        color: 'white', 
                                        padding: '4px 8px', 
                                        borderRadius: '4px', 
                                        fontSize: '0.8rem' 
                                      }}>
                                        {flow.invoke_method}
                                      </span>
                                    </div>
                                  </div>
                                  <div>
                                    <strong>Current Node:</strong>
                                    <div style={{ color: isDarkMode ? '#999999' : '#6b7280', fontSize: '0.9rem' }}>
                                      {(() => {
                                        if (flow.status === 'active' && logs.length > 0) {
                                          // For active flows, find the most recent node execution from logs
                                          const nodeExecutionLogs = logs
                                            .filter(log => log.message.includes('**Node: ID='))
                                            .reverse(); // Get most recent first
                                          
                                          if (nodeExecutionLogs.length > 0) {
                                            const latestNodeLog = nodeExecutionLogs[0];
                                            const match = latestNodeLog.message.match(/\*\*Node: ID=(\d+), Type=(\w+)/);
                                            if (match) {
                                              const [, nodeId, nodeType] = match;
                                              return (
                                                <div>
                                                  <span style={{ fontWeight: 'bold', color: '#17a2b8' }}>
                                                    {nodeType.toUpperCase()}
                                                  </span>
                                                  <div style={{ fontSize: '0.8rem', color: '#999' }}>
                                                    ID: {nodeId}
                                                  </div>
                                                </div>
                                              );
                                            }
                                          }
                                        }
                                        
                                        // Fallback for non-active flows or when no logs available
                                        return (
                                          <div>
                                            {flow.current_node_name || 'Completed'}
                                            {flow.current_node_id && (
                                              <div style={{ fontSize: '0.8rem', color: '#999' }}>{flow.current_node_id}</div>
                                            )}
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  </div>
                                  <div>
                                    <strong>Duration:</strong>
                                    <div style={{ color: isDarkMode ? '#999999' : '#6b7280', fontSize: '0.9rem' }}>
                                      {flow.total_duration_formatted || formatDuration(flow).total}
                                    </div>
                                  </div>
                                  <div>
                                    <strong>Type:</strong>
                                    <div style={{ marginTop: '4px' }}>
                                      {flow.is_parent_flow ? (
                                        <span style={{ 
                                          backgroundColor: '#17a2b8', 
                                          color: 'white', 
                                          padding: '2px 6px', 
                                          borderRadius: '4px', 
                                          fontSize: '0.7rem' 
                                        }}>
                                          Parent Flow
                                        </span>
                                      ) : (
                                        <span style={{ 
                                          backgroundColor: '#6c757d', 
                                          color: 'white', 
                                          padding: '2px 6px', 
                                          borderRadius: '4px', 
                                          fontSize: '0.7rem' 
                                        }}>
                                          Child Flow
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                
                                {flow.message && (
                                  <div style={{ marginBottom: '15px' }}>
                                    <strong>Message:</strong>
                                    <div style={{ 
                                      marginTop: '5px', 
                                      padding: '10px', 
                                      backgroundColor: isDarkMode ? '#0a0a0a' : '#ffffff',
                                      borderRadius: '4px',
                                      border: `1px solid ${isDarkMode ? '#1a1a1a' : '#e5e7eb'}`,
                                      color: isDarkMode ? '#e0e0e0' : '#374151'
                                    }}>
                                      {flow.message}
                                    </div>
                                  </div>
                                )}

                                {flow.completion_response && (
                                  <div style={{ marginBottom: '15px' }}>
                                    <strong>Completion Response:</strong>
                                    <div style={{ 
                                      marginTop: '5px', 
                                      padding: '10px', 
                                      backgroundColor: isDarkMode ? 'rgba(0,214,122,0.1)' : 'rgba(0,214,122,0.1)',
                                      borderRadius: '4px',
                                      border: `1px solid ${isDarkMode ? 'rgba(0,214,122,0.2)' : 'rgba(0,214,122,0.2)'}`,
                                      color: isDarkMode ? '#00d67a' : '#00d67a'
                                    }}>
                                      {flow.completion_response}
                                    </div>
                                  </div>
                                )}
                              </div>

                              <hr style={{ 
                                border: 'none', 
                                borderTop: `1px solid ${isDarkMode ? '#1a1a1a' : '#e5e7eb'}`, 
                                margin: '20px 0' 
                              }} />
                              
                              {/* Logs header */}
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                <h4 style={{ margin: '0', color: isDarkMode ? '#e0e0e0' : '#374151' }}>
                                  {flow.status === 'active' ? 'Live Logs' : 'Preserved Logs'}
                                  {flow.status === 'stopped' && (
                                    <small style={{ color: '#999999', fontWeight: 'normal' }}> (Limited - cleaned on stop)</small>
                                  )}
                                </h4>
                                {flow.status === 'active' && (
                                  <button 
                                    className="btn-dark" 
                                    onClick={() => handleClearLogs(flow.runtime_id)}
                                    style={{ fontSize: '12px', padding: '5px 10px' }}
                                  >
                                    Clear Logs
                                  </button>
                                )}
                              </div>
                              
                              {/* Logs content - New JSON-based rendering */}
                              <div style={{ 
                                maxHeight: '600px', 
                                overflowY: 'auto',
                                backgroundColor: isDarkMode ? '#0d0d0d' : '#ffffff',
                                border: `1px solid ${isDarkMode ? '#1a1a1a' : '#e5e7eb'}`,
                                borderRadius: '8px',
                                fontFamily: 'inherit'
                              }}>
                                {logs.length === 0 ? (
                                  <p style={{ color: '#999999', fontStyle: 'italic', margin: '20px', fontSize: '16px', textAlign: 'center' }}>
                                    {flow.status === 'stopped' 
                                      ? 'Logs were cleaned up when agent was stopped' 
                                      : 'No logs available'}
                                  </p>
                                ) : (
                                  (() => {
                                    // Group logs by node execution
                                    const nodeGroups = {};
                                    const systemLogs = [];
                                    
                                    logs.forEach((log, idx) => {
                                      let logData = null;
                                      let isJsonLog = false;
                                      
                                      try {
                                        if (typeof log.message === 'object' && log.message !== null) {
                                          logData = log.message;
                                          isJsonLog = logData.type && typeof logData.type === 'string';
                                        } else if (typeof log.message === 'string') {
                                          logData = JSON.parse(log.message);
                                          isJsonLog = logData && typeof logData === 'object' && logData.type && typeof logData.type === 'string';
                                        } else {
                                          logData = String(log.message);
                                          isJsonLog = false;
                                        }
                                      } catch {
                                        logData = typeof log.message === 'string' ? log.message : String(log.message || '');
                                        isJsonLog = false;
                                      }
                                      
                                      if (isJsonLog && logData.run_id) {
                                        // Group by run_id for node executions
                                        if (!nodeGroups[logData.run_id]) {
                                          nodeGroups[logData.run_id] = {
                                            run_id: logData.run_id,
                                            node_id: logData.node_id,
                                            node_name: logData.node_name || 'Unknown Node',
                                            node_type: logData.node_type || 'unknown',
                                            start_time: null,
                                            end_time: null,
                                            processing_time: null,
                                            status: 'running',
                                            message: null,
                                            result: {},
                                            logs: [],
                                            order_start: idx // Track first occurrence index for chronological sorting
                                          };
                                        }
                                        
                                        const group = nodeGroups[logData.run_id];
                                        group.logs.push({ ...log, logData, isJsonLog });
                                        
                                        // Extract key information
                                        if (logData.type === 'node_start') {
                                          group.start_time = logData.start_time;
                                          group.node_type = logData.node_type || group.node_type;
                                          group.node_name = logData.node_name || group.node_name;
                                          if (logData.message) group.message = logData.message;
                                          
                                          // Extract parameters that show what variables are being considered
                                          if (logData.params) {
                                            if (logData.params.conditions) group.result.conditions = logData.params.conditions;
                                            if (logData.params.prompt) group.result.prompt = logData.params.prompt;
                                            if (logData.params.vault_name) group.result.vault_name = logData.params.vault_name;
                                          }
                                          
                                          // Extract all variables present in the data
                                          if (logData.variable) group.result.variable_value = logData.variable;
                                          if (logData.variables) group.result.variables = logData.variables;
                                          if (logData.params && logData.params.variable) group.result.param_variable = logData.params.variable;
                                          if (logData.params && logData.params.variables) group.result.param_variables = logData.params.variables;
                                        } else if (logData.type === 'node_complete') {
                                          group.end_time = logData.end_time;
                                          group.processing_time = logData.processing_time;
                                          group.status = 'completed';
                                          if (logData.message) group.message = logData.message;
                                          
                                          // Extract results
                                          if (logData.evaluation_result !== undefined) group.result.evaluation_result = logData.evaluation_result;
                                          if (logData.output_path) group.result.output_path = logData.output_path;
                                          if (logData.recognition_result !== undefined) group.result.recognition_result = logData.recognition_result;
                                          if (logData.variable_set) group.result.variable_set = logData.variable_set;
                                          if (logData.value_assigned) group.result.value_assigned = logData.value_assigned;
                                          if (logData.generated_content) group.result.generated_content = logData.generated_content;
                                          if (logData.response_length) group.result.response_length = logData.response_length;
                                          if (logData.selected_option) group.result.selected_option = logData.selected_option;
                                          if (logData.number_response !== undefined) group.result.number_response = logData.number_response;
                                          
                                          // Extract all variables present in completion data  
                                          if (logData.variable) group.result.variable_value = logData.variable;
                                          if (logData.variables) group.result.variables = logData.variables;
                                          if (logData.params && logData.params.variable) group.result.param_variable = logData.params.variable;
                                          if (logData.params && logData.params.variables) group.result.param_variables = logData.params.variables;
                                        } else if (logData.type === 'first_token') {
                                          // Capture time to first token
                                          group.result.time_to_first_token = logData.time_to_first_token;
                                        }
                                      } else {
                                        // System logs
                                        systemLogs.push({ ...log, logData, isJsonLog, order: idx });
                                      }
                                    });
                                    
                                    // Convert to array and sort by start time
                                    const sortedNodes = Object.values(nodeGroups).sort((a, b) => {
                                      return (a.start_time || 0) - (b.start_time || 0);
                                    });
                                    
                                    // Build unified timeline array (nodes + system logs) sorted by first appearance order
                                    const timelineItems = [
                                      ...sortedNodes.map(n => ({ type: 'node', data: n, order: n.order_start || 0 })),
                                      ...systemLogs.map(l => ({ type: 'system', data: l, order: l.order || 0 }))
                                    ].sort((a, b) => a.order - b.order);
                                    
                                    return (
                                      <div style={{ padding: '15px' }}>
                                        <div style={{ 
                                          backgroundColor: isDarkMode ? '#0d0d0d' : '#ffffff',
                                          borderRadius: '6px',
                                          padding: '15px',
                                          marginBottom: '20px',
                                          border: `1px solid ${isDarkMode ? '#0d0d0d' : '#ffffff'}`
                                        }}>
                                          <h5 style={{ margin: '0 0 10px 0', color: isDarkMode ? '#fff' : '#212529' }}>
                                            Execution Timeline
                                          </h5>
                                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px' }}>
                                            <div>
                                              <span style={{ color: '#6c757d', fontSize: '13px' }}>Total Nodes</span>
                                              <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#00d67a' }}>
                                                {sortedNodes.length}
                                              </div>
                                            </div>
                                            <div>
                                              <span style={{ color: '#6c757d', fontSize: '13px' }}>System Logs</span>
                                              <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#28a745' }}>
                                                {systemLogs.length}
                                              </div>
                                            </div>
                                            <div>
                                              <span style={{ color: '#6c757d', fontSize: '13px' }}>Total Time</span>
                                              <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#ffc107' }}>
                                                {sortedNodes.reduce((sum, node) => sum + (node.processing_time || 0), 0).toFixed(2)}s
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                        
                                        {/* Node Executions */}
                                        {timelineItems.map((item) => {
                                          if (item.type === 'system') {
                                            const logEntry = item.data;
                                            const message = typeof logEntry.logData === 'string' ? logEntry.logData : (logEntry.logData.message || JSON.stringify(logEntry.logData));
                                            return (
                                              <div key={`sys-${item.order}`} style={{
                                                backgroundColor: isDarkMode ? '#0d0d0d' : '#ffffff',
                                                border: `1px solid ${isDarkMode ? '#3a3d45' : '#dee2e6'}`,
                                                borderRadius: '8px',
                                                marginBottom: '8px',
                                                padding: '8px 12px'
                                              }}>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                                                    <i className={
                                                      message.includes('VAR <>') ? 'fas fa-equals' :
                                                      message.includes('Processing Flow Name:') ? 'fas fa-play' :
                                                      message.includes('**Node: ID=') ? 'fas fa-cog' :
                                                      message.includes('Vault Change:') ? 'fas fa-database' :
                                                      message.includes('Model Platform Change:') ? 'fas fa-robot' :
                                                      message.includes('get vault time:') ? 'fas fa-clock' :
                                                      message.includes('Sending variables to Zapier:') ? 'fas fa-link' :
                                                      'fas fa-info-circle'
                                                    } style={{ color: '#6c757d', fontSize: '12px', flexShrink: 0 }}></i>
                                                    
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                      {/* Enhanced system log parsing - horizontal layout */}
                                                      {(() => {
                                                        if (message.includes('VAR <>')) {
                                                          const match = message.match(/VAR <> (.+?): (.+)/);
                                                          if (match) {
                                                            return (
                                                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                <span style={{ fontSize: '11px', fontWeight: 'bold', color: isDarkMode ? '#e9ecef' : '#212529' }}>
                                                                  Variable:
                                                                </span>
                                                                <span style={{ fontSize: '11px', color: isDarkMode ? '#00d67a' : '#00d67a', fontFamily: 'monospace' }}>
                                                                  {match[1]} = {match[2].length > 50 ? match[2].substring(0, 50) + '...' : match[2]}
                                                                </span>
                                                              </div>
                                                            );
                                                          }
                                                        }
                                                        
                                                        if (message.includes('Processing Flow Name:')) {
                                                          const flowName = message.replace('Processing Flow Name: ', '');
                                                          return (
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                              <span style={{ fontSize: '11px', fontWeight: 'bold', color: isDarkMode ? '#e9ecef' : '#212529' }}>
                                                                Flow Started:
                                                              </span>
                                                              <span style={{ fontSize: '11px', color: isDarkMode ? '#999999' : '#6b7280' }}>
                                                                {flowName}
                                                              </span>
                                                            </div>
                                                          );
                                                        }
                                                        
                                                        if (message.includes('**Node: ID=')) {
                                                          const match = message.match(/\*\*Node: ID=(\d+), Type=(\w+)/);
                                                          if (match) {
                                                            return (
                                                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                <span style={{ fontSize: '11px', fontWeight: 'bold', color: isDarkMode ? '#e9ecef' : '#212529' }}>
                                                                  Node:
                                                                </span>
                                                                <span style={{ fontSize: '11px', color: isDarkMode ? '#999999' : '#6b7280' }}>
                                                                  ID {match[1]} ({match[2].toUpperCase()})
                                                                </span>
                                                              </div>
                                                            );
                                                          }
                                                        }
                                                        
                                                        if (message.includes('Vault Change:')) {
                                                          const match = message.match(/Vault Change: (.+?) → (.+)/);
                                                          if (match) {
                                                            return (
                                                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                <span style={{ fontSize: '11px', fontWeight: 'bold', color: isDarkMode ? '#e9ecef' : '#212529' }}>
                                                                  Vault:
                                                                </span>
                                                                <span style={{ fontSize: '11px', color: isDarkMode ? '#999999' : '#6b7280' }}>
                                                                  {match[1]} → {match[2]}
                                                                </span>
                                                              </div>
                                                            );
                                                          }
                                                        }
                                                        
                                                        if (message.includes('Model Platform Change:')) {
                                                          const match = message.match(/Model Platform Change: (.+?) → (.+)/);
                                                          if (match) {
                                                            return (
                                                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                <span style={{ fontSize: '11px', fontWeight: 'bold', color: isDarkMode ? '#e9ecef' : '#212529' }}>
                                                                  Model:
                                                                </span>
                                                                <span style={{ fontSize: '11px', color: isDarkMode ? '#999999' : '#6b7280' }}>
                                                                  {match[1]} → {match[2]}
                                                                </span>
                                                              </div>
                                                            );
                                                          }
                                                        }
                                                        
                                                        if (message.includes('get vault time:')) {
                                                          const match = message.match(/get vault time: (.+?) seconds/);
                                                          if (match) {
                                                            return (
                                                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                <span style={{ fontSize: '11px', fontWeight: 'bold', color: isDarkMode ? '#e9ecef' : '#212529' }}>
                                                                  Vault Query:
                                                                </span>
                                                                <span style={{ fontSize: '11px', color: isDarkMode ? '#999999' : '#6b7280' }}>
                                                                  {match[1]}s
                                                                </span>
                                                              </div>
                                                            );
                                                          }
                                                        }
                                                        
                                                        if (message.includes('Sending variables to Zapier:')) {
                                                          const match = message.match(/Sending variables to Zapier: (.+)/);
                                                          if (match) {
                                                            try {
                                                              const vars = JSON.parse(match[1]);
                                                              return (
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                  <span style={{ fontSize: '11px', fontWeight: 'bold', color: isDarkMode ? '#e9ecef' : '#212529' }}>
                                                                    Zapier:
                                                                  </span>
                                                                  <span style={{ fontSize: '11px', color: isDarkMode ? '#999999' : '#6b7280' }}>
                                                                    {Object.keys(vars).join(', ')}
                                                                  </span>
                                                                </div>
                                                              );
                                                            } catch {
                                                              return (
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                  <span style={{ fontSize: '11px', fontWeight: 'bold', color: isDarkMode ? '#e9ecef' : '#212529' }}>
                                                                    Zapier:
                                                                  </span>
                                                                  <span style={{ fontSize: '11px', color: isDarkMode ? '#999999' : '#6b7280' }}>
                                                                    {match[1].substring(0, 50)}...
                                                                  </span>
                                                                </div>
                                                              );
                                                            }
                                                          }
                                                        }
                                                        
                                                        if (message.includes('vars...:')) {
                                                          const match = message.match(/vars\.\.\.: (.+)/);
                                                          if (match) {
                                                            try {
                                                              const vars = JSON.parse(match[1]);
                                                              return (
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                  <span style={{ fontSize: '11px', fontWeight: 'bold', color: isDarkMode ? '#e9ecef' : '#212529' }}>
                                                                    Variables:
                                                                  </span>
                                                                  <span style={{ fontSize: '11px', color: isDarkMode ? '#999999' : '#6b7280' }}>
                                                                    {Object.keys(vars).slice(0, 3).join(', ')}{Object.keys(vars).length > 3 ? ` +${Object.keys(vars).length - 3} more` : ''}
                                                                  </span>
                                                                </div>
                                                              );
                                                            } catch {
                                                              return (
                                                                <div style={{ fontSize: '11px', color: isDarkMode ? '#e9ecef' : '#212529' }}>
                                                                  {message.substring(0, 60)}...
                                                                </div>
                                                              );
                                                            }
                                                          }
                                                        }
                                                        
                                                        // Default display for unmatched system logs
                                                        return (
                                                          <div style={{ fontSize: '11px', color: isDarkMode ? '#e9ecef' : '#212529' }}>
                                                            {message.length > 80 ? message.substring(0, 80) + '...' : message}
                                                          </div>
                                                        );
                                                      })()}
                                                    </div>
                                                  </div>
                                                  
                                                  {logEntry.formatted_time && (
                                                    <div style={{ 
                                                      fontSize: '10px', 
                                                      color: '#6c757d', 
                                                      fontFamily: 'monospace',
                                                      flexShrink: 0
                                                    }}>
                                                      {logEntry.formatted_time}
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            );
                                          }

                                          const node = item.data;
                                          const isExpanded = expandedNodes.has(node.run_id);
                                          
                                          return (
                                            <div key={node.run_id} style={{
                                              backgroundColor: isDarkMode ? '#0d0d0d' : '#ffffff',
                                              border: `1px solid ${isDarkMode ? '#3a3d45' : '#dee2e6'}`,
                                              borderRadius: '8px',
                                              marginBottom: '12px',
                                              overflow: 'hidden',
                                              transition: 'all 0.2s ease'
                                            }}>
                                              {/* Node Header */}
                                              <div 
                                                style={{
                                                  padding: '16px',
                                                  cursor: 'pointer',
                                                  borderBottom: isExpanded ? `1px solid ${isDarkMode ? '#3a3d45' : '#dee2e6'}` : 'none',
                                                  transition: 'background-color 0.2s'
                                                }}
                                                onClick={() => {
                                                  const newExpanded = new Set(expandedNodes);
                                                  if (newExpanded.has(node.run_id)) {
                                                    newExpanded.delete(node.run_id);
                                                  } else {
                                                    newExpanded.add(node.run_id);
                                                  }
                                                  setExpandedNodes(newExpanded);
                                                }}
                                                onMouseEnter={(e) => {
                                                  if (!isExpanded) e.currentTarget.style.backgroundColor = isDarkMode ? '#111111' : '#f3f4f6';
                                                }}
                                                onMouseLeave={(e) => {
                                                  if (!isExpanded) e.currentTarget.style.backgroundColor = 'transparent';
                                                }}
                                              >
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                    <i className={getNodeIconClass(node.node_type)} style={{ 
                                                      fontSize: '18px', 
                                                      color: '#6c757d',
                                                      width: '20px',
                                                      textAlign: 'center'
                                                    }}></i>
                                                    <div>
                                            <div style={{ 
                                              fontWeight: 'bold',
                                                        fontSize: '16px', 
                                                        color: isDarkMode ? '#fff' : '#212529',
                                                        marginBottom: '2px'
                                            }}>
                                                        {node.node_name}
                                            </div>
                                                      <div style={{ fontSize: '12px', color: '#6c757d' }}>
                                                        {node.node_type.toUpperCase()}
                                                        {node.start_time && (
                                                          <span> • {new Date(node.start_time * 1000).toLocaleTimeString()}</span>
                                                        )}
                                                        {node.processing_time && (
                                                          <span style={{ 
                                                            color: node.processing_time > 2 ? '#dc3545' : node.processing_time > 1 ? '#ffc107' : '#28a745',
                                                            fontWeight: 'bold'
                                                          }}>
                                                            {' '}• {node.processing_time.toFixed(2)}s
                                                          </span>
                                                        )}
                                                        {node.result.time_to_first_token && node.node_type === 'respond' && (
                                                          <span style={{ 
                                                            color: '#fd7e14',
                                                            fontWeight: 'bold'
                                                          }}>
                                                            {' '}• TTFT: {node.result.time_to_first_token.toFixed(2)}s
                                                          </span>
                                                        )}
                                                      </div>
                                                      
                                                      {/* Show variables being considered */}
                                                      {(node.result.conditions || node.result.prompt || (node.node_type === 'recognize' && (node.result.variable_value || node.result.variables || node.result.param_variable || node.result.param_variables))) && (
                                                         <div style={{ 
                                                           fontSize: '11px', 
                                                           color: '#6c757d',
                                                           marginTop: '4px',
                                                           fontStyle: 'italic'
                                                         }}>
                                                           {node.result.conditions && (
                                                             <span
                                                               style={{
                                                                 cursor: 'pointer',
                                                                 textDecoration: 'underline',
                                                                 color: '#00d67a',
                                                                 transition: 'color 0.2s'
                                                               }}
                                                               onClick={(e) => {
                                                                 e.stopPropagation();
                                                                 const newExpanded = new Set(expandedNodes);
                                                                 if (!newExpanded.has(node.run_id)) {
                                                                   newExpanded.add(node.run_id);
                                                                   setExpandedNodes(newExpanded);
                                                                 }
                                                                 setTimeout(() => {
                                                                   const element = document.getElementById(`node-${node.run_id}-details`);
                                                                   if (element) {
                                                                     element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                                                                   }
                                                                 }, 100);
                                                               }}
                                                               onMouseEnter={(e) => {
                                                                 e.target.style.color = '#00b368';
                                                               }}
                                                               onMouseLeave={(e) => {
                                                                 e.target.style.color = '#00d67a';
                                                               }}
                                                               title="Click to see full conditions"
                                                             >
                                                               Evaluating: {node.result.conditions.map(c => c.variable).join(', ')}
                                                             </span>
                                                           )}
                                                           {node.result.prompt && !node.result.conditions && (
                                                             <span
                                                               style={{
                                                                 cursor: 'pointer',
                                                                 textDecoration: 'underline',
                                                                 color: '#00d67a',
                                                                 transition: 'color 0.2s'
                                                               }}
                                                               onClick={(e) => {
                                                                 e.stopPropagation(); // Prevent the parent onClick from firing
                                                                 const newExpanded = new Set(expandedNodes);
                                                                 if (!newExpanded.has(node.run_id)) {
                                                                   newExpanded.add(node.run_id);
                                                                   setExpandedNodes(newExpanded);
                                                                 }
                                                                 // Scroll to the expanded section after a brief delay
                                                                 setTimeout(() => {
                                                                   const element = document.getElementById(`node-${node.run_id}-details`);
                                                                   if (element) {
                                                                     element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                                                                   }
                                                                 }, 100);
                                                               }}
                                                               onMouseEnter={(e) => {
                                                                 e.target.style.color = '#00b368';
                                                               }}
                                                               onMouseLeave={(e) => {
                                                                 e.target.style.color = '#00d67a';
                                                               }}
                                                               title="Click to see full prompt"
                                                             >
                                                               "{node.result.prompt.length > 60 ? node.result.prompt.substring(0, 60) + '...' : node.result.prompt}"
                                                             </span>
                                                           )}
                                                           {node.node_type === 'recognize' && !node.result.conditions && !node.result.prompt && (node.result.variable_value || node.result.variables || node.result.param_variable || node.result.param_variables) && (
                                                             <span>
                                                               Recognizing in: {
                                                                 node.result.variable_value || 
                                                                 node.result.param_variable ||
                                                                 (node.result.variables && Object.keys(node.result.variables).join(', ')) ||
                                                                 (node.result.param_variables && Object.keys(node.result.param_variables).join(', '))
                                                               }
                                                             </span>
                                                           )}
                                                         </div>
                                                       )}
                                          </div>
                                                  </div>
                                                  
                                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    {/* Timing Badge - Show prominently */}
                                                    {node.processing_time && (
                                                      <span style={{
                                                        backgroundColor: node.processing_time > 2 ? 
                                                          (isDarkMode ? '#4a2b2b' : '#f8d7da') : 
                                                          node.processing_time > 1 ? 
                                                          (isDarkMode ? '#4a3f2b' : '#fff3cd') : 
                                                          (isDarkMode ? 'rgba(0,214,122,0.1)' : 'rgba(0,214,122,0.1)'),
                                                        color: node.processing_time > 2 ? 
                                                          (isDarkMode ? '#f8a8a8' : '#721c24') : 
                                                          node.processing_time > 1 ? 
                                                          (isDarkMode ? '#f4c842' : '#856404') : 
                                                          (isDarkMode ? '#00d67a' : '#00d67a'),
                                                        padding: '4px 8px',
                                                        borderRadius: '4px',
                                                        fontSize: '11px',
                                                        fontWeight: 'bold',
                                                        border: `1px solid ${node.processing_time > 2 ? 
                                                          (isDarkMode ? '#6b3737' : '#f5c6cb') : 
                                                          node.processing_time > 1 ? 
                                                          (isDarkMode ? '#6b5f37' : '#ffeaa7') : 
                                                          (isDarkMode ? 'rgba(0,214,122,0.2)' : 'rgba(0,214,122,0.2)')}`
                                                      }}>
                                                        {node.processing_time.toFixed(2)}s
                                                      </span>
                                                    )}
                                                    
                                                    {/* Time to First Token Badge - Only for respond nodes */}
                                                    {node.result.time_to_first_token && node.node_type === 'respond' && (
                                                      <span style={{
                                                        backgroundColor: isDarkMode ? 'rgba(255,193,7,0.1)' : 'rgba(255,193,7,0.1)',
                                                        color: isDarkMode ? '#ffc107' : '#856404',
                                                        padding: '4px 8px',
                                                        borderRadius: '4px',
                                                        fontSize: '11px',
                                                        fontWeight: 'bold',
                                                        border: `1px solid ${isDarkMode ? '#ffc107' : '#ffeaa7'}`
                                                      }}>
                                                        TTFT: {node.result.time_to_first_token.toFixed(2)}s
                                                      </span>
                                                    )}
                                                    
                                                    {/* Variables - Show first and most prominently */}
                                                    {node.result.variable_set && (
                                                      <span style={{
                                                        backgroundColor: isDarkMode ? 'rgba(0,214,122,0.1)' : 'rgba(0,214,122,0.1)',
                                                        color: isDarkMode ? '#00d67a' : '#00d67a',
                                                        padding: '5px 10px',
                                                        borderRadius: '5px',
                                              fontSize: '12px',
                                                        fontFamily: 'monospace',
                                              fontWeight: 'bold',
                                                        border: `1px solid ${isDarkMode ? 'rgba(0,214,122,0.2)' : 'rgba(0,214,122,0.2)'}`
                                            }}>
                                                        {node.result.variable_set} = {
                                                          node.result.value_assigned || 
                                                          (node.result.generated_content ? 
                                                            (node.result.generated_content.length > 50 ? 
                                                              node.result.generated_content.substring(0, 50) + '...' : 
                                                              node.result.generated_content) : 
                                                            '...')
                                                        }
                                            </span>
                                          )}
                                                    
                                                    {/* Show all variables present in the data */}
                                                    {node.result.variable_value && (
                                                      <span style={{
                                                        backgroundColor: isDarkMode ? '#2d3a2d' : '#e8f5e8',
                                                        color: isDarkMode ? '#00d67a' : '#00d67a',
                                                        padding: '4px 8px',
                                                        borderRadius: '4px',
                                                        fontSize: '11px',
                                                        fontFamily: 'monospace',
                                                        fontWeight: 'bold',
                                                        border: `1px solid ${isDarkMode ? 'rgba(0,214,122,0.2)' : 'rgba(0,214,122,0.2)'}`
                                                      }}>
                                                        variable: "{node.result.variable_value}"
                                                      </span>
                                                    )}
                                                    
                                                    {/* Show multiple variables if present */}
                                                    {node.result.variables && Object.keys(node.result.variables).length > 0 && (
                                                      <span style={{
                                                        backgroundColor: isDarkMode ? '#2d3a2d' : '#e8f5e8',
                                                        color: isDarkMode ? '#00d67a' : '#00d67a',
                                                        padding: '4px 8px',
                                                        borderRadius: '4px',
                                                        fontSize: '11px',
                                                        fontFamily: 'monospace',
                                                        fontWeight: 'bold',
                                                        border: `1px solid ${isDarkMode ? 'rgba(0,214,122,0.2)' : 'rgba(0,214,122,0.2)'}`
                                                      }}>
                                                        {Object.entries(node.result.variables).map(([key, value]) => 
                                                          `${key}: "${value}"`
                                                        ).join(', ')}
                                                      </span>
                                                    )}
                                                    
                                                    {/* If/Then results */}
                                                    {node.node_type === 'if_then' && (
                                                      <>
                                                        {node.result.evaluation_result !== undefined && (
                                                          <span style={{
                                                            backgroundColor: node.result.evaluation_result ? 
                                                              (isDarkMode ? 'rgba(0,214,122,0.1)' : 'rgba(0,214,122,0.1)') : 
                                                              (isDarkMode ? '#4a2b2b' : '#f8d7da'),
                                                            color: node.result.evaluation_result ? 
                                                              (isDarkMode ? '#00d67a' : '#00d67a') : 
                                                              (isDarkMode ? '#f8a8a8' : '#721c24'),
                                                            padding: '4px 8px',
                                                            borderRadius: '4px',
                                                            fontSize: '12px',
                                                            fontWeight: 'bold'
                                                          }}>
                                                            {node.result.evaluation_result ? 'TRUE' : 'FALSE'}
                                                          </span>
                                                        )}
                                                        {node.result.output_path && (
                                                          <span style={{
                                                            backgroundColor: isDarkMode ? '#17a2b8' : '#d1ecf1',
                                                            color: isDarkMode ? '#fff' : '#0c5460',
                                                            padding: '4px 8px',
                                                            borderRadius: '4px',
                                                            fontSize: '11px'
                                                          }}>
                                                            → {node.result.output_path}
                                                          </span>
                                                        )}
                                                      </>
                                                    )}
                                                    
                                                    {/* Recognition results */}
                                                    {node.result.recognition_result !== undefined && (
                                                      <span style={{
                                                        backgroundColor: node.result.recognition_result ? 
                                                          (isDarkMode ? 'rgba(0,214,122,0.1)' : 'rgba(0,214,122,0.1)') : 
                                                          (isDarkMode ? '#4a2b2b' : '#f8d7da'),
                                                        color: node.result.recognition_result ? 
                                                          (isDarkMode ? '#00d67a' : '#00d67a') : 
                                                          (isDarkMode ? '#f8a8a8' : '#721c24'),
                                                        padding: '4px 8px',
                                                        borderRadius: '4px',
                                                        fontSize: '12px',
                                                        fontWeight: 'bold'
                                                      }}>
                                                        {node.result.recognition_result ? 'YES' : 'NO'}
                                                      </span>
                                                    )}
                                                    
                                                    {/* List match results */}
                                                    {node.result.selected_option && (
                                                      <span style={{
                                                        backgroundColor: isDarkMode ? '#2a1e3d' : '#e2e3ff',
                                                        color: isDarkMode ? '#c8a2c8' : '#383d41',
                                                        padding: '4px 8px',
                                                        borderRadius: '4px',
                                                        fontSize: '11px'
                                                      }}>
                                                        Selected: {node.result.selected_option}
                                                      </span>
                                                    )}
                                                    
                                                    {/* Number extraction results */}
                                                    {node.result.number_response !== undefined && (
                                                      <span style={{
                                                        backgroundColor: isDarkMode ? 'rgba(255,193,7,0.1)' : 'rgba(255,193,7,0.1)',
                                                        color: isDarkMode ? '#ffc107' : '#856404',
                                                        padding: '4px 8px',
                                                        borderRadius: '4px',
                                                        fontSize: '11px',
                                                        fontFamily: 'monospace'
                                                      }}>
                                                        #{node.result.number_response}
                                                      </span>
                                                    )}
                                                    
                                                    {/* Response length for content generation */}
                                                    {node.result.response_length && !node.result.variable_set && (
                                                      <span style={{
                                                        backgroundColor: isDarkMode ? '#0d0d0d' : '#ffffff',
                                                        color: '#6c757d',
                                                        padding: '4px 8px',
                                                        borderRadius: '4px',
                                                        fontSize: '11px'
                                                      }}>
                                                        {node.result.response_length} chars
                                                      </span>
                                                    )}
                                                    
                                                    <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`} style={{ 
                                                      color: '#6c757d',
                                                      fontSize: '12px'
                                                    }}></i>
                                        </div>
                                                </div>
                                                
                                                {/* Node message if available */}
                                                {node.message && (
                                          <div style={{ 
                                            marginTop: '8px', 
                                            fontSize: '13px',
                                                    color: isDarkMode ? '#999999' : '#6b7280',
                                                    fontStyle: 'italic'
                                                  }}>
                                                    {node.message}
                                                  </div>
                                                )}
                                              </div>
                                              
                                              {/* Expanded Details */}
                                              {isExpanded && (
                                                <div 
                                                  id={`node-${node.run_id}-details`}
                                                  style={{
                                                    padding: '16px',
                                                    backgroundColor: isDarkMode ? '#23262d' : '#f8f9fa'
                                                  }}
                                                >
                                                  {/* Show full prompt if available */}
                                                  {node.result.prompt && (
                                                    <div style={{
                                                      marginBottom: '16px',
                                                      padding: '12px',
                                                      backgroundColor: isDarkMode ? '#1a1d23' : '#ffffff',
                                                      border: `2px solid #00d67a`,
                                                      borderRadius: '6px'
                                                    }}>
                                                      <h6 style={{ 
                                                        margin: '0 0 8px 0', 
                                                        color: '#00d67a',
                                                        fontSize: '13px',
                                                        fontWeight: 'bold'
                                                      }}>
                                                        <i className="fas fa-quote-left" style={{ marginRight: '6px' }}></i>
                                                        Full Prompt
                                                      </h6>
                                                      <div style={{
                                                        fontSize: '12px',
                                                        fontFamily: 'monospace',
                                                        color: isDarkMode ? '#e9ecef' : '#212529',
                                                        lineHeight: '1.4',
                                                        whiteSpace: 'pre-wrap'
                                                      }}>
                                                        "{node.result.prompt}"
                                                      </div>
                                                    </div>
                                                  )}
                                                  
                                                  {/* Show full conditions if available */}
                                                  {node.result.conditions && (
                                                    <div style={{
                                                      marginBottom: '16px',
                                                      padding: '12px',
                                                      backgroundColor: isDarkMode ? '#1a1d23' : '#ffffff',
                                                      border: `2px solid #17a2b8`,
                                                      borderRadius: '6px'
                                                    }}>
                                                      <h6 style={{ 
                                                        margin: '0 0 8px 0', 
                                                        color: '#17a2b8',
                                                        fontSize: '13px',
                                                        fontWeight: 'bold'
                                                      }}>
                                                        <i className="fas fa-code-branch" style={{ marginRight: '6px' }}></i>
                                                        Full Conditions
                                                      </h6>
                                                      <div style={{
                                                        fontSize: '12px',
                                                        color: isDarkMode ? '#e9ecef' : '#212529',
                                                        lineHeight: '1.4'
                                                      }}>
                                                        {node.result.conditions.map((condition, idx) => (
                                                          <div key={idx} style={{
                                                            marginBottom: '8px',
                                                            padding: '8px',
                                                            backgroundColor: isDarkMode ? '#0d0d0d' : '#ffffff',
                                                            borderRadius: '4px',
                                                            border: `1px solid ${isDarkMode ? '#3a3d45' : '#dee2e6'}`
                                                          }}>
                                                            <div style={{ fontFamily: 'monospace', marginBottom: '4px' }}>
                                                              <strong>Variable:</strong> {condition.variable}
                                                            </div>
                                                            <div style={{ fontFamily: 'monospace', marginBottom: '4px' }}>
                                                              <strong>Operator:</strong> {condition.operator}
                                                            </div>
                                                            <div style={{ fontFamily: 'monospace' }}>
                                                              <strong>Value:</strong> {condition.value}
                                                            </div>
                                                          </div>
                                                        ))}
                                                      </div>
                                                    </div>
                                                  )}
                                                  
                                                  <div style={{
                                                    backgroundColor: isDarkMode ? '#1a1d23' : '#ffffff',
                                                    border: `1px solid ${isDarkMode ? '#3a3d45' : '#dee2e6'}`,
                                                    borderRadius: '4px',
                                                    maxHeight: '500px',
                                                    overflowY: 'auto'
                                                  }}>
                                                    <table style={{
                                                      width: '100%',
                                                      borderCollapse: 'collapse',
                                                      fontSize: '12px'
                                                    }}>
                                                      <thead>
                                                        <tr style={{
                                                          backgroundColor: isDarkMode ? '#0d0d0d' : '#ffffff',
                                                          borderBottom: `2px solid ${isDarkMode ? '#3a3d45' : '#dee2e6'}`
                                                        }}>
                                                          <th style={{
                                                            padding: '12px 8px',
                                                            textAlign: 'left',
                                                            fontWeight: 'bold',
                                                            color: isDarkMode ? '#999999' : '#6b7280',
                                                            fontSize: '11px',
                                                            width: '120px'
                                                          }}>TIME</th>
                                                          <th style={{
                                                            padding: '12px 8px',
                                                            textAlign: 'left',
                                                            fontWeight: 'bold',
                                                            color: isDarkMode ? '#999999' : '#6b7280',
                                                            fontSize: '11px',
                                                            width: '100px'
                                                          }}>TYPE</th>
                                                          <th style={{
                                                            padding: '12px 8px',
                                                            textAlign: 'left',
                                                            fontWeight: 'bold',
                                                            color: isDarkMode ? '#999999' : '#6b7280',
                                                            fontSize: '11px'
                                                          }}>DETAILS</th>
                                                          <th style={{
                                                            padding: '12px 8px',
                                                            textAlign: 'left',
                                                            fontWeight: 'bold',
                                                            color: isDarkMode ? '#999999' : '#6b7280',
                                                            fontSize: '11px',
                                                            width: '150px'
                                                          }}>METRICS</th>
                                                        </tr>
                                                      </thead>
                                                      <tbody>
                                                    
                                                    {node.logs.map((log, idx) => {
                                                      const logData = log.logData;
                                                      const isJsonLog = log.isJsonLog;
                                                      
                                                      // Skip duplicate recognition result messages
                                                      if (typeof logData === 'object' && logData.message && 
                                                          typeof logData.message === 'string' && 
                                                          logData.message.includes('Recognition result:')) {
                                                        return null;
                                                      }
                                                      
                                                      return (
                                                        <tr key={idx} style={{
                                                          borderBottom: `1px solid ${isDarkMode ? '#1a1a1a' : '#e5e7eb'}`
                                                        }}>
                                                          {/* Time Cell */}
                                                          <td style={{
                                                            padding: '12px 8px',
                                                            verticalAlign: 'top',
                                                            color: '#6c757d',
                                                            fontSize: '11px',
                                                            fontFamily: 'monospace'
                                                          }}>
                                                            {log.formatted_time}
                                                          </td>
                                                          
                                                          {/* Type Cell */}
                                                          <td style={{
                                                            padding: '12px 8px',
                                                            verticalAlign: 'top'
                                                          }}>
                                                            <span style={{
                                                              backgroundColor: isDarkMode ? '#1a2a3a' : '#f0f8ff',
                                                              color: '#17a2b8',
                                                              padding: '4px 8px',
                                                              borderRadius: '4px',
                                                              fontSize: '10px',
                                                              fontWeight: 'bold',
                                                              display: 'inline-block'
                                                            }}>
                                                              {logData.type || 'legacy'}
                                                            </span>
                                                          </td>
                                                          
                                                          {/* Details Cell */}
                                                          <td style={{
                                                            padding: '12px 8px',
                                                            verticalAlign: 'top',
                                                            color: isDarkMode ? '#fff' : '#212529'
                                                          }}>
                                                            {isJsonLog && typeof logData === 'object' ? (
                                                              <div>
                                                                {logData.type === 'node_start' && (
                                                                  <div>
                                                                    <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '13px' }}>
                                                                      {logData.node_name || logData.node_type}
                                                                    </div>
                                                                    
                                                                    {logData.message && (
                                                                      <div style={{ 
                                                                        color: isDarkMode ? '#999999' : '#6b7280', 
                                                                        fontSize: '11px', 
                                                                        marginBottom: '12px',
                                                                        padding: '6px 8px',
                                                                        backgroundColor: isDarkMode ? '#0d0d0d' : '#ffffff',
                                                                        borderRadius: '4px',
                                                                        border: `1px solid ${isDarkMode ? '#0d0d0d' : '#ffffff'}`
                                                                      }}>
                                                                        {logData.message}
                                                                      </div>
                                                                    )}
                                                                    
                                                                    {logData.params && Object.keys(logData.params).length > 0 && (
                                                                      <div style={{ marginTop: '8px' }}>
                                                                        <div style={{ 
                                                                          fontWeight: 'bold', 
                                                                          fontSize: '10px', 
                                                                          color: isDarkMode ? '#999999' : '#6b7280',
                                                                          marginBottom: '6px',
                                                                          textTransform: 'uppercase',
                                                                          letterSpacing: '0.5px'
                                                                        }}>
                                                                          Parameters
                                                                        </div>
                                                                        <div style={{ 
                                                                          backgroundColor: isDarkMode ? '#23262d' : '#f8f9fa',
                                                                          border: `1px solid ${isDarkMode ? '#0d0d0d' : '#ffffff'}`,
                                                                          borderRadius: '4px',
                                                                          padding: '8px'
                                                                        }}>
                                                                          {Object.entries(logData.params).map(([key, value]) => (
                                                                            <div key={key} style={{ 
                                                                              display: 'flex',
                                                                              marginBottom: '4px',
                                                                              fontSize: '10px'
                                                                            }}>
                                                                              <div style={{ 
                                                                                fontWeight: 'bold',
                                                                                minWidth: '100px',
                                                                                color: isDarkMode ? '#999999' : '#6b7280',
                                                                                paddingRight: '8px'
                                                                              }}>
                                                                                {key}:
                                                                              </div>
                                                                              <div style={{ 
                                                                                flex: 1,
                                                                                fontFamily: key === 'prompt' ? 'inherit' : 'monospace',
                                                                                color: isDarkMode ? '#e0e0e0' : '#374151',
                                                                                wordBreak: 'break-word'
                                                                              }}>
                                                                                {key === 'prompt' && String(value).length > 200 
                                                                                  ? String(value).substring(0, 200) + '...'
                                                                                  : String(value)
                                                                                }
                                                                              </div>
                                                                            </div>
                                                                          ))}
                                                                        </div>
                                                                      </div>
                                                                    )}
                                                                  </div>
                                                                )}
                                                                
                                                                {logData.type === 'node_complete' && (
                                                                  <div>
                                                                    <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '13px' }}>
                                                                      Node Completed Successfully
                                                                    </div>
                                                                    
                                                                    {logData.message && (
                                                                      <div style={{ 
                                                                        color: isDarkMode ? '#999999' : '#6b7280', 
                                                                        fontSize: '11px',
                                                                        marginBottom: '8px',
                                                                        padding: '6px 8px',
                                                                        backgroundColor: isDarkMode ? '#0d0d0d' : '#ffffff',
                                                                        borderRadius: '4px'
                                                                      }}>
                                                                        {logData.message}
                                                                      </div>
                                                                    )}
                                                                    
                                                                    {/* Act Node Specific - Script Output */}
                                                                    {logData.script_output && (
                                                                      <div style={{ 
                                                                        backgroundColor: isDarkMode ? '#1a2d1a' : '#e8f5e8',
                                                                        color: isDarkMode ? '#00d67a' : '#00d67a',
                                                                        padding: '12px',
                                                                        borderRadius: '6px',
                                                                        fontSize: '11px',
                                                                        marginBottom: '12px',
                                                                        border: `1px solid ${isDarkMode ? 'rgba(0,214,122,0.2)' : 'rgba(0,214,122,0.2)'}`,
                                                                        fontFamily: 'monospace'
                                                                      }}>
                                                                        <div style={{ fontWeight: 'bold', marginBottom: '8px', fontFamily: 'sans-serif' }}>
                                                                          <i className="fas fa-terminal" style={{ marginRight: '6px' }}></i>
                                                                          Script Output
                                                                        </div>
                                                                        <pre style={{ 
                                                                          margin: 0, 
                                                                          whiteSpace: 'pre-wrap',
                                                                          fontFamily: 'monospace',
                                                                          fontSize: '10px',
                                                                          lineHeight: '1.4'
                                                                        }}>
                                                                          {logData.script_output}
                                                                        </pre>
                                                                      </div>
                                                                    )}
                                                                    
                                                                    {/* Act Node Specific - Error Traceback */}
                                                                    {logData.error_traceback && (
                                                                      <div style={{ 
                                                                        backgroundColor: isDarkMode ? '#4a2b2b' : '#f8d7da',
                                                                        color: isDarkMode ? '#f8a8a8' : '#721c24',
                                                                        padding: '12px',
                                                                        borderRadius: '6px',
                                                                        fontSize: '11px',
                                                                        marginBottom: '12px',
                                                                        border: `1px solid ${isDarkMode ? '#6b3737' : '#f5c6cb'}`,
                                                                        fontFamily: 'monospace'
                                                                      }}>
                                                                        <div style={{ fontWeight: 'bold', marginBottom: '8px', fontFamily: 'sans-serif' }}>
                                                                          <i className="fas fa-exclamation-triangle" style={{ marginRight: '6px' }}></i>
                                                                          Error Traceback
                                                                        </div>
                                                                        <pre style={{ 
                                                                          margin: 0, 
                                                                          whiteSpace: 'pre-wrap',
                                                                          fontFamily: 'monospace',
                                                                          fontSize: '10px',
                                                                          lineHeight: '1.4'
                                                                        }}>
                                                                          {logData.error_traceback}
                                                                        </pre>
                                                                      </div>
                                                                    )}
                                                                    
                                                                    {/* Act Node Specific - Script Content */}
                                                                    {logData.script && (
                                                                      <div style={{ 
                                                                        backgroundColor: isDarkMode ? '#2a2a3a' : '#f8f9ff',
                                                                        color: isDarkMode ? '#c8c8ff' : '#383d41',
                                                                        padding: '12px',
                                                                        borderRadius: '6px',
                                                                        fontSize: '11px',
                                                                        marginBottom: '12px',
                                                                        border: `1px solid ${isDarkMode ? '#4a4a6a' : '#d1ecf1'}`,
                                                                        fontFamily: 'monospace'
                                                                      }}>
                                                                        <div style={{ fontWeight: 'bold', marginBottom: '8px', fontFamily: 'sans-serif' }}>
                                                                          <i className="fas fa-code" style={{ marginRight: '6px' }}></i>
                                                                          Executed Script
                                                                        </div>
                                                                        <pre style={{ 
                                                                          margin: 0, 
                                                                          whiteSpace: 'pre-wrap',
                                                                          fontFamily: 'monospace',
                                                                          fontSize: '10px',
                                                                          lineHeight: '1.4'
                                                                        }}>
                                                                          {logData.script}
                                                                        </pre>
                                                                      </div>
                                                                    )}
                                                                    
                                                                    {/* Standard Variable Assignment */}
                                                                    {logData.variable_set && !logData.script_output && (
                                                                      <div style={{ 
                                                                        backgroundColor: isDarkMode ? 'rgba(0,214,122,0.1)' : 'rgba(0,214,122,0.1)',
                                                                        color: isDarkMode ? '#00d67a' : '#00d67a',
                                                                        padding: '8px',
                                                                        borderRadius: '4px',
                                                                        fontSize: '11px',
                                                                        marginBottom: '8px',
                                                                        border: `1px solid ${isDarkMode ? 'rgba(0,214,122,0.2)' : 'rgba(0,214,122,0.2)'}`
                                                                      }}>
                                                                        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Variable Assignment</div>
                                                                        <div style={{ fontFamily: 'monospace' }}>
                                                                          {logData.variable_set} = {
                                                                            (logData.value_assigned || logData.generated_content || '').length > 100 
                                                                              ? (logData.value_assigned || logData.generated_content || '').substring(0, 100) + '...'
                                                                              : (logData.value_assigned || logData.generated_content || '')
                                                                          }
                                                                        </div>
                                                                      </div>
                                                                    )}
                                                                    
                                                                    {logData.error && !logData.error_traceback && (
                                                                      <div style={{ 
                                                                        backgroundColor: isDarkMode ? '#4a2b2b' : '#f8d7da',
                                                                        color: isDarkMode ? '#f8a8a8' : '#721c24',
                                                                        padding: '8px',
                                                                        borderRadius: '4px',
                                                                        fontSize: '11px',
                                                                        border: `1px solid ${isDarkMode ? '#6b3737' : '#f5c6cb'}`
                                                                      }}>
                                                                        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Error</div>
                                                                        <div>{logData.error}</div>
                                                                      </div>
                                                                    )}
                                                                  </div>
                                                                )}
                                                                
                                                                {['first_token', 'static_response', 'retry_attempt', 'child_flow_log'].includes(logData.type) && (
                                                                  <div>
                                                                    <div style={{ fontWeight: 'bold', marginBottom: '6px', fontSize: '13px' }}>
                                                                      {logData.type === 'first_token' && 'First Token Received'}
                                                                      {logData.type === 'static_response' && 'Static Response Used'}
                                                                      {logData.type === 'retry_attempt' && 'Retry Attempt'}
                                                                      {logData.type === 'child_flow_log' && `Child Flow: ${logData.flow_name}`}
                                                                    </div>
                                                                    
                                                                    {(logData.message || logData.child_log) && (
                                                                      <div style={{ 
                                                                        color: logData.type === 'retry_attempt' ? '#ffc107' : (isDarkMode ? '#999999' : '#6b7280'), 
                                                                        fontSize: '11px',
                                                                        padding: '6px 8px',
                                                                        backgroundColor: isDarkMode ? '#0d0d0d' : '#ffffff',
                                                                        borderRadius: '4px'
                                                                      }}>
                                                                        {logData.message || logData.child_log}
                                                                      </div>
                                                                    )}
                                                                  </div>
                                                                )}
                                                                
                                                                {!['node_start', 'node_complete', 'first_token', 'static_response', 'retry_attempt', 'child_flow_log'].includes(logData.type) && (
                                                                  <div>
                                                                    <div style={{ fontWeight: 'bold', marginBottom: '6px', fontSize: '13px', textTransform: 'capitalize' }}>
                                                                      {logData.type.replace('_', ' ')}
                                                                    </div>
                                                                    {logData.message && (
                                                                      <div style={{ 
                                                                        fontSize: '11px',
                                                                        color: isDarkMode ? '#999999' : '#6b7280'
                                                                      }}>
                                                                        {logData.message}
                                                                      </div>
                                                                    )}
                                                                  </div>
                                                                )}
                                                              </div>
                                                            ) : (
                                                              <div style={{ fontSize: '11px', color: isDarkMode ? '#999999' : '#6b7280' }}>
                                                                {typeof logData === 'string' ? logData : JSON.stringify(logData)}
                                                              </div>
                                                            )}
                                                          </td>
                                                          
                                                          {/* Metrics Cell */}
                                                          <td style={{
                                                            padding: '12px 8px',
                                                            verticalAlign: 'top',
                                                            fontSize: '11px'
                                                          }}>
                                                            {isJsonLog && typeof logData === 'object' && (
                                                              <div>
                                                                {logData.processing_time && (
                                                                  <div style={{ color: '#28a745', fontWeight: 'bold', marginBottom: '4px' }}>
                                                                    {logData.processing_time.toFixed(3)}s
                                                                  </div>
                                                                )}
                                                                {logData.time_to_first_token && (
                                                                  <div style={{ color: '#fd7e14', fontWeight: 'bold', marginBottom: '4px' }}>
                                                                    TTFT: {logData.time_to_first_token.toFixed(3)}s
                                                                  </div>
                                                                )}
                                                                {logData.response_length && (
                                                                  <div style={{ color: '#6c757d', marginBottom: '2px' }}>
                                                                    {logData.response_length} chars
                                                                  </div>
                                                                )}
                                                                {logData.recognition_result !== undefined && (
                                                                  <div style={{ color: logData.recognition_result ? '#28a745' : '#dc3545', fontWeight: 'bold', marginBottom: '2px' }}>
                                                                    {logData.recognition_result ? 'YES' : 'NO'}
                                                                  </div>
                                                                )}
                                                                {logData.evaluation_result !== undefined && (
                                                                  <div style={{ color: logData.evaluation_result ? '#28a745' : '#dc3545', fontWeight: 'bold', marginBottom: '2px' }}>
                                                                    {logData.evaluation_result ? 'TRUE' : 'FALSE'}
                                                                  </div>
                                                                )}
                                                                {logData.number_response !== undefined && (
                                                                  <div style={{ color: '#17a2b8', fontWeight: 'bold', marginBottom: '2px' }}>
                                                                    #{logData.number_response}
                                                                  </div>
                                                                )}
                                                                {logData.selected_option && (
                                                                  <div style={{ color: '#6f42c1', fontWeight: 'bold', marginBottom: '2px' }}>
                                                                    {logData.selected_option}
                                                                  </div>
                                                                )}
                                                                {/* Act Node Specific Metrics */}
                                                                {logData.script_output_length && (
                                                                  <div style={{ color: '#17a2b8', fontWeight: 'bold', marginBottom: '2px' }}>
                                                                    {logData.script_output_length} chars
                                                                  </div>
                                                                )}
                                                                {logData.script_execution_time && (
                                                                  <div style={{ color: '#fd7e14', fontWeight: 'bold', marginBottom: '2px' }}>
                                                                    Exec: {logData.script_execution_time.toFixed(3)}s
                                                                  </div>
                                                                )}
                                                                {logData.script_success !== undefined && (
                                                                  <div style={{ color: logData.script_success ? '#28a745' : '#dc3545', fontWeight: 'bold', marginBottom: '2px' }}>
                                                                    SCRIPT: {logData.script_success ? 'SUCCESS' : 'FAILED'}
                                                                  </div>
                                                                )}
                                                                {logData.vars_changed !== undefined && (
                                                                  <div style={{ color: '#6c757d', marginBottom: '2px' }}>
                                                                    {logData.vars_changed} vars changed
                                                                  </div>
                                                                )}
                                                                {logData.changed_vars_count !== undefined && (
                                                                  <div style={{ color: '#6c757d', marginBottom: '2px' }}>
                                                                    {logData.changed_vars_count} vars
                                                                  </div>
                                                                )}
                                                                {['execution_success', 'email_success', 'download_success', 'search_success', 'add_success', 'operation_success'].map(key => {
                                                                  if (logData[key] !== undefined) {
                                                                    return (
                                                                      <div key={key} style={{ color: logData[key] ? '#28a745' : '#dc3545', fontWeight: 'bold', marginBottom: '2px' }}>
                                                                        {logData[key] ? 'SUCCESS' : 'FAILED'}
                                                                      </div>
                                                                    );
                                                                  }
                                                                  return null;
                                                                })}
                                                                {logData.error && (
                                                                  <div style={{ color: '#dc3545', fontWeight: 'bold', fontSize: '10px' }}>
                                                                    ERROR
                                                                  </div>
                                                                )}
                                                              </div>
                                                            )}
                                                          </td>
                                                        </tr>
                                                      );
                                                                                                          }).filter(Boolean)}
                                                      </tbody>
                                                    </table>
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                        
                                        {/* (Integrated system logs, old section disabled) */}
                                        {false && systemLogs.length > 0 && (
                                          <div style={{
                                            backgroundColor: isDarkMode ? '#0d0d0d' : '#ffffff',
                                            border: `1px solid ${isDarkMode ? '#3a3d45' : '#dee2e6'}`,
                                            borderRadius: '8px',
                                            marginTop: '20px',
                                            overflow: 'hidden'
                                          }}>
                                            <div style={{
                                              padding: '16px',
                                              borderBottom: `1px solid ${isDarkMode ? '#3a3d45' : '#dee2e6'}`,
                                              backgroundColor: isDarkMode ? '#111111' : '#f3f4f6'
                                            }}>
                                              <h6 style={{ 
                                                margin: '0', 
                                                color: isDarkMode ? '#fff' : '#212529',
                                                fontSize: '14px',
                                                fontWeight: 'bold'
                                              }}>
                                                <i className="fas fa-cogs" style={{ marginRight: '8px', color: '#6c757d' }}></i>
                                                System Logs ({systemLogs.length})
                                              </h6>
                                            </div>
                                            <div style={{ padding: '12px' }}>
                                              {systemLogs.map((log, idx) => {
                                                let message = typeof log.logData === 'string' ? log.logData : JSON.stringify(log.logData);
                                                let icon = 'fas fa-info-circle';
                                                let badgeColor = '#6c757d';
                                                
                                                if (typeof message === 'string') {
                                                  if (message.includes('Processing Flow Name:')) {
                                                    icon = 'fas fa-play';
                                                    badgeColor = '#6f42c1';
                                                  } else if (message.includes('VAR <>')) {
                                                    icon = 'fas fa-equals';
                                                    badgeColor = '#28a745';
                                                  }
                                                }
                                                
                                                return (
                                                  <div key={idx} style={{ 
                                                    marginBottom: '8px',
                                                    padding: '8px',
                                                    borderLeft: `3px solid ${badgeColor}`,
                                                    backgroundColor: isDarkMode ? '#23262d' : '#f8f9fa',
                                                    borderRadius: '4px'
                                                  }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                      <span style={{ color: '#6c757d', fontSize: '11px' }}>
                                                        {log.formatted_time}
                                                      </span>
                                                      <i className={icon} style={{ 
                                                        fontSize: '12px', 
                                                        color: badgeColor 
                                                      }}></i>
                                                      <span style={{ 
                                                        color: isDarkMode ? '#fff' : '#212529',
                                                        fontSize: '12px',
                                                        fontFamily: (typeof message === 'string' && message.includes('VAR <>')) ? 'monospace' : 'inherit'
                                                      }}>
                                                        {message}
                                                      </span>
                                                    </div>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })()
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </StyledTable>
        </StyledTableContainer>

        {/* Bottom spacing */}
        <br /><br /><br />
      </div>
    </div>
  );
};

export default FlowRuntimeDashboard; 